import i18n from '../i18n/config';

/**
 * A translated fallback message for code that runs outside a React component.
 *
 * ## Why this exists
 *
 * `AuthContext`, the refresh interceptor and the push service are plain
 * modules — no component, so no `useTranslation` hook. They therefore carried
 * 25 hardcoded Arabic sentences between them, shown to every member in every
 * language.
 *
 * They are shown at the worst possible moment, too. `apiErrorMessage(error, x)`
 * falls back to `x` exactly when the server said nothing usable — a dropped
 * connection, a transport failure — so the member is already confused when the
 * app answers in a language they do not read.
 *
 * Reading off the i18n singleton is safe here because every one of these call
 * sites runs on an event (a rejected request, a click), long after i18n has
 * initialised — never during module evaluation. `t` is called fresh each time,
 * so switching language takes effect without a reload.
 *
 * @param {string} key  a key under the `authErrors.*` namespace
 * @returns {string}    the message in the member's language
 */
export function authError(key) {
  try {
    const translated = i18n.t(`authErrors.${key}`);
    // A missing key renders as the key path itself. Showing "authErrors.login"
    // to a member is worse than showing nothing, so fall back to the generic
    // sentence, which is present in all four languages.
    if (translated && !translated.startsWith('authErrors.')) return translated;
    const generic = i18n.t('authErrors.unexpected');
    return generic.startsWith('authErrors.') ? '' : generic;
  } catch {
    // i18n must never be able to turn an error message into a second error.
    return '';
  }
}

export default authError;
